import { useCallback, useEffect, useRef } from "react";
import { Location as HistoryLocation } from "history";
import { ViewportCenterCircle } from "@newComponents/common/ViewportCenterCircle";
import { useRoute } from "@newHooks/route/useRoute";
import { decryptAuthKeys, generateAuthKeys, SessionDecryptionError } from "./libs/authKey.lib";
import { AuthSessionStorage } from "./libs/AuthSessionStorage";
import { Session } from "./libs/common";
import { generatedRedirectUrl, generateSsoUrl, isInvalidTimeStamp, SSO_GW_KEYS } from "./libs/sso.lib";

type SSOProps = {
  ssoBaseUrl: string;
  defaultRedirect?: string;
  onSessionCreated: (session: Session, redirectTo: string) => void;
  onSessionExpired?: (session: Session | null) => void;
  onDecryptSessionError: () => void;
};

export function SSO({ ssoBaseUrl, defaultRedirect, onSessionCreated, onSessionExpired, onDecryptSessionError }: SSOProps) {
  const { location } = useRoute();
  const callRef = useRef<boolean>(false);

  // SSO에서 받아온 세션 키를 복호화하고, 유효성을 검사하여 세션을 생성한다.
  const handleRequestLogin = useCallback(
    ({ uuid, key }: { uuid: string; key: string }) => {
      const privateKey = AuthSessionStorage.getPrivateKey(uuid) ?? "";

      try {
        const decryptedSession = decryptAuthKeys(key, privateKey);
        const timeStamp = AuthSessionStorage.getTimeStamp(decryptedSession);

        if (timeStamp === null || isInvalidTimeStamp(timeStamp)) {
          const redirectTo = AuthSessionStorage.getRedirectUrl(uuid) ?? defaultRedirect;

          AuthSessionStorage.setTimeStamp(decryptedSession);

          callRef.current = true;
          onSessionCreated(decryptedSession, redirectTo || "/");
          return;
        }

        // `onSessionCreated`가 호출되지 않은 경우만 세션 만료로 판단하고, `onSessionExpired`를 호출한다.
        if (!callRef.current) {
          onSessionExpired?.(decryptedSession);
        }
      } catch (error) {
        if (error instanceof SessionDecryptionError) {
          onDecryptSessionError?.();
        } else {
          // eslint-disable-next-line no-console
          console.error("error", error);
        }
      }
    },
    [defaultRedirect, onDecryptSessionError, onSessionCreated, onSessionExpired]
  );

  const handleRequestSso = useCallback(() => {
    const authKeys = generateAuthKeys();
    const { uuid, privateKey } = authKeys;

    AuthSessionStorage.setPrivateKey(uuid, privateKey);
    AuthSessionStorage.setRedirectUrl(uuid, generatedRedirectUrl(location as HistoryLocation<{ from?: Location }>, defaultRedirect));
    const ssoUrl = generateSsoUrl(ssoBaseUrl, authKeys);

    document.location.href = ssoUrl;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, defaultRedirect, ssoBaseUrl]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const key = queryParams.get(SSO_GW_KEYS.KEY);
    const uuid = queryParams.get(SSO_GW_KEYS.UUID);

    const hasGwKeys = !!key && !!uuid;

    if (hasGwKeys) {
      handleRequestLogin({ uuid, key });
      return;
    }

    handleRequestSso();
  }, [handleRequestLogin, handleRequestSso, location]);

  return <ViewportCenterCircle />;
}
